import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtMinHM } from "@/lib/utils";

const TimerContext = createContext(null);

export function TimerProvider({ children }) {
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const tick = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) { setActive(null); return; }
    try {
      const res = await api.get("/timer/active");
      setActive(res.data.active);
    } catch (e) { /* noop */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (tick.current) clearInterval(tick.current);
    if (active?.started_at) {
      const compute = () => {
        const start = new Date(active.started_at).getTime();
        setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
      };
      compute();
      tick.current = setInterval(compute, 1000);
    } else {
      setElapsed(0);
    }
    return () => tick.current && clearInterval(tick.current);
  }, [active]);

  const start = async ({ project_id, task_id = null, description = "" }) => {
    const res = await api.post("/timer/start", { project_id, task_id, description });
    setActive(res.data);
    toast.success("Temporizador iniciado");
  };

  const stop = async () => {
    const res = await api.post("/timer/stop");
    setActive(null);
    const mins = res.data.minutes;
    toast.success(`Registradas ${fmtMinHM(mins)}`);
    return res.data;
  };

  return (
    <TimerContext.Provider value={{ active, elapsed, start, stop, refresh }}>
      {children}
    </TimerContext.Provider>
  );
}

export function useTimer() {
  return useContext(TimerContext);
}

export function formatElapsed(s) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}
